import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { LICENSE_INFO_API_MAX_DATE, usePTPayPlm0100 } from '@/features/api';
import { useBrowserUILanguage } from '@/hooks/useBrowserUILanguage';
import { useLoginUserInfo } from '@/hooks/useLoginUserInfo';
import {
  convertDateToString,
  convertYyyyMMddhhmmssToDateUserTimeZone,
  dateFormatByLang,
} from '@/utils/date';

import {
  CONTRACTED_TEXT,
  SERIAL_CHANGE_CONTENT,
  SERIAL_REGISTRATION_CONTENT,
  SUBSCRIPTION_PURCHASED_TEXT,
} from '../constants';

import { PLAN_INFO_STATE, usePlan } from './usePlan';

/**
 * 失敗時に実行したい関数の型
 */
export type FailedFunction = () => void;

/**
 * 本カスタムフックのプロパティ
 */
type Props = {
  // 失敗時に実行したい処理
  onFailedFun?: FailedFunction;
};

/**
 * プランダイアログ hooks
 *
 * @param param0
 */
export const usePlanDialog = ({ onFailedFun }: Props) => {
  const { openProductSelect, openServiceList } = usePTPayPlm0100();
  const { licenseInfoStatus, licenseExp, serial, planInfoResult } = usePlan();
  const { language } = useBrowserUILanguage();
  const { isSerialPlan, isFreePlan } = useLoginUserInfo();

  const [isOpenSerialLinkDialog, setIsOpenSerialLinkDialog] =
    useState<boolean>(false);

  /**
   * プロパティに指定された関数Ref
   */
  const onFailedFuncRef = useRef<FailedFunction>();
  useEffect(() => {
    onFailedFuncRef.current = onFailedFun;
  }, [onFailedFun]);

  /**
   * 再ログインが必要か否か(true=必要)
   */
  const [needsLoginAgain, setNeedsLoginAgain] = useState<boolean>(false);

  /**
   * ライセンス確認失敗エラーメッセージを表示する場合のみ、
   * プロパティに指定された関数を実行
   */
  useEffect(() => {
    if (planInfoResult === PLAN_INFO_STATE.ERROR) {
      onFailedFuncRef.current?.();
    }
  }, [planInfoResult]);

  /**
   * [ライセンス購入]ボタンがクリックされた場合の処理
   */
  const onClickPurchaseButton = useCallback(() => {
    // PTPayサーバの商品選択画面を表示(別タブ)
    openProductSelect();
    // 本システム上には契約状態更新後の再ログインダイアログを表示して再ログインを促す
    setNeedsLoginAgain(true);
  }, [openProductSelect]);

  /**
   * [アカウント情報]ボタンがクリックされた場合の処理
   */
  const onClickAccountButton = useCallback(() => {
    // PTPayサーバのサービス一覧画面を表示(別タブ)
    openServiceList();
    // 本システム上には契約状態更新後の再ログインダイアログを表示して再ログインを促す
    setNeedsLoginAgain(true);
  }, [openServiceList]);

  /**
   * [アカウント情報]ボタンを非表示にするか否か
   */
  const isHiddenAccountButton = useMemo(() => {
    if (isSerialPlan) {
      // 旧プランの場合は非表示
      return true;
    }

    return false;
  }, [isSerialPlan]);

  /**
   * ライセンス有効期限の表示用テキスト
   */
  const licenseExpDate = useMemo(() => {
    // お試しプランの場合は表示しない
    if (isFreePlan) {
      return undefined;
    }

    // ライセンス期限が取得できなかった場合は表示しない
    if (!licenseExp) {
      return undefined;
    }

    // ライセンス期限が無期限の時は表示しない
    if (licenseExp === LICENSE_INFO_API_MAX_DATE) {
      return undefined;
    }

    // ユーザのタイムゾーンでの日付を表示する
    const date = convertYyyyMMddhhmmssToDateUserTimeZone(licenseExp);

    const format = dateFormatByLang(language);

    return convertDateToString(date, format);
  }, [isFreePlan, language, licenseExp]);

  /**
   * [シリアル連携]リンクがクリックされた場合の処理
   */
  const onClickSerialLink = useCallback(() => {
    setIsOpenSerialLinkDialog(true);
  }, []);

  /**
   * シリアル連携ダイアログの閉じるボタンがクリックされた場合の処理
   */
  const onClickSerialLinkDialogClose = useCallback(() => {
    setIsOpenSerialLinkDialog(false);
  }, []);

  /**
   * シリアル連携処理成功時に実行させたい処理
   */
  const handleSerialLinkSuccess = useCallback(() => {
    onClickSerialLinkDialogClose();
    setNeedsLoginAgain(true);
  }, [onClickSerialLinkDialogClose]);

  /**
   * プランダイアログ内の説明文
   */
  const planDialogText = useMemo(() => {
    if (isSerialPlan) {
      return SUBSCRIPTION_PURCHASED_TEXT;
    }

    return CONTRACTED_TEXT;
  }, [isSerialPlan]);

  /**
   * プランダイアログ内に表示するコンテンツ
   */
  const planDialogContent = useMemo(() => {
    if (isFreePlan) {
      return SERIAL_REGISTRATION_CONTENT;
    }

    return SERIAL_CHANGE_CONTENT;
  }, [isFreePlan]);

  /**
   * [シリアル連携]リンクを表示するか否
   */
  const shouldShowSerialLink = useMemo(() => {
    if (isSerialPlan) {
      return false;
    }

    return true;
  }, [isSerialPlan]);

  return {
    // ライセンス確認状況
    licenseInfoStatus,
    // ライセンス有効期限
    licenseExpDate,
    // [ライセンス購入]ボタンがクリックされた場合の処理
    onClickPurchaseButton,
    // ライセンス確認APIから返却されたシリアル
    serial,
    // [アカウント情報]ボタンがクリックされた場合の処理
    onClickAccountButton,
    // [アカウント情報]ボタンを非表示にするか否
    isHiddenAccountButton,
    // 再ログインが必要か否か(true=必要)
    needsLoginAgain,
    // プラン確認状態
    planInfoResult,
    // [シリアル連携]リンクを表示するか否
    shouldShowSerialLink,
    // シリアル連携ダイアログを開くか否
    isOpenSerialLinkDialog,
    // [シリアル連携]リンクがクリックされた場合の処理
    onClickSerialLink,
    // シリアル連携ダイアログの閉じるボタンがクリックされた場合の処理
    onClickSerialLinkDialogClose,
    // シリアル連携処理成功時に実行させたい処理
    handleSerialLinkSuccess,
    // プランダイアログ内の説明文
    planDialogText,
    // プランダイアログ内に表示するコンテンツ
    planDialogContent,
  };
};
